import type { DtPcSceneKey } from '@/assets/js/dt-pc-route';
import { getDtPcSceneKey } from '@/assets/js/dt-pc-route';
import { useStore } from '@/store';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

/** jump_info.sku，与 dt.json 一致（原 shop_name 已改为 short_name） */
export interface DtTopBarSku {
  id?: number | string;
  sku?: string;
  shop_sku?: string;
  short_name?: string;
}

/** 与 components/layout/dt.json 中 list 单项一致；图片字段与 M 端对齐为 `img_url`，旧配置 `img` 会在归一化时合并 */
export interface DtPcItem {
  img_url?: string;
  /** 旧魔方/本地 dt.json 可能仍使用，注水时会合并到 `img_url` */
  img?: string;
  text?: string;
  /** 文案颜色 */
  color?: string;
  activity_name?: string;
  begin_timestamp?: number;
  end_timestamp?: number;
  jump_info?: {
    jump_type?: number;
    Jump_target?: string | number;
    sku?: DtTopBarSku;
    product_group?: { id?: string | number; name?: string };
    [k: string]: unknown;
  };
  /** 列表项若单独配条带底色（可选） */
  bg_color?: string;
}

export interface DtPcBlock {
  list?: DtPcItem[];
  /** 相邻两条顶通轮播间隔（秒），对应 dt.json `second_interval`（旧字段 `speed` 仍兼容读取） */
  second_interval?: number | string;
  bg_color?: string;
}

export type DtPcConfig = Partial<Record<DtPcSceneKey, DtPcBlock>>;

/** 将历史字段 jump_info.sku.shop_name 迁到 short_name（与 dt.json 一致） */
function normalizeDtConfigSkuNames(config: DtPcConfig | null): DtPcConfig | null {
  if (!config || typeof config !== 'object') {
    return config;
  }
  for (const sceneKey of Object.keys(config) as DtPcSceneKey[]) {
    const block = config[sceneKey];
    if (!block || typeof block !== 'object') {
      continue;
    }
    const list = block.list;
    if (!Array.isArray(list)) {
      continue;
    }
    for (const row of list) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const ji = (row as DtPcItem).jump_info;
      if (!ji || typeof ji !== 'object') {
        continue;
      }
      const sku = ji.sku;
      if (!sku || typeof sku !== 'object') {
        continue;
      }
      const s = sku as Record<string, unknown>;
      if (s.short_name == null && s.shop_name != null) {
        s.short_name = s.shop_name;
        delete s.shop_name;
      }
    }
  }
  return config;
}

/** 列表项图片：优先 `img_url`，兼容旧字段 `img`（归一化后应优先读 `img_url`） */
export function dtPcItemImgUrlForIdentity(item: DtPcItem | null | undefined): string {
  if (!item) {
    return '';
  }
  const u = item.img_url ?? item.img;
  return typeof u === 'string' ? u : '';
}

function hydrateDtPcItemImgUrlFields(config: DtPcConfig | null): DtPcConfig | null {
  if (!config || typeof config !== 'object') {
    return config;
  }
  for (const sceneKey of Object.keys(config) as DtPcSceneKey[]) {
    const block = config[sceneKey];
    if (!block || typeof block !== 'object') {
      continue;
    }
    const list = block.list;
    if (!Array.isArray(list)) {
      continue;
    }
    for (const row of list) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const r = row as Record<string, unknown>;
      const primary = r.img_url;
      const legacy = r.img;
      const resolved =
        typeof primary === 'string' && primary !== ''
          ? primary
          : typeof legacy === 'string' && legacy !== ''
            ? legacy
            : '';
      if (resolved) {
        (row as DtPcItem).img_url = resolved;
      }
    }
  }
  return config;
}

/** BFF `data.ding_tong_banner`（由请求体 `ding_tong_banner: true` 返回）归一化为与 dt.json 同结构的纯对象 */
export function normalizeDtPcPayload(dingTongBanner: unknown): DtPcConfig | null {
  const raw = dingTongBanner;
  if (raw == null) {
    return null;
  }
  let v: unknown = raw;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof v !== 'object' || v === null) {
    return null;
  }
  const o = v as Record<string, unknown>;
  const inner = (o.json_value as DtPcConfig | undefined) ?? (o as DtPcConfig);
  if (!inner || typeof inner !== 'object') {
    return null;
  }
  return hydrateDtPcItemImgUrlFields(normalizeDtConfigSkuNames(inner));
}

function parseDtPcFromStore(raw: unknown): DtPcConfig | null {
  if (raw == null) {
    return null;
  }
  let v: unknown = raw;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof v !== 'object' || v === null) {
    return null;
  }
  const o = v as Record<string, unknown>;
  const inner = (o.json_value as DtPcConfig | undefined) ?? (o as DtPcConfig);
  if (!inner || typeof inner !== 'object') {
    return null;
  }
  return hydrateDtPcItemImgUrlFields(normalizeDtConfigSkuNames(inner));
}

function toUnixSec(v: unknown): number | null {
  if (v == null || v === '') {
    return null;
  }
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) {
    return null;
  }
  return Math.floor(n);
}

export function isDtPcItemActive(item: DtPcItem, nowSec: number): boolean {
  const beginTs = toUnixSec(item.begin_timestamp);
  const endTs = toUnixSec(item.end_timestamp);
  if (beginTs != null && endTs != null) {
    return nowSec >= beginTs && nowSec <= endTs;
  }
  if (beginTs != null && endTs == null) {
    return nowSec >= beginTs;
  }
  if (beginTs == null && endTs != null) {
    return nowSec <= endTs;
  }
  return false;
}

/**
 * 顶通 `data.ding_tong_banner`：按路由场景 + 起止时间戳过滤后的列表与轮播间隔
 */
export function useDtPcTopBar() {
  const store = useStore();
  const { bffSsrData, systemTimestamp } = storeToRefs(store);
  const route = useRoute();

  const anchorWallMs = ref<number | null>(null);
  const anchorServerSec = ref<number | null>(null);

  function pinAnchorFromStore() {
    const ts = systemTimestamp.value;
    if (typeof ts === 'number' && Number.isFinite(ts) && ts > 0) {
      anchorServerSec.value = Math.floor(ts);
      if (import.meta.client) {
        anchorWallMs.value = Date.now();
      }
    }
  }

  watch(
    () => systemTimestamp.value,
    () => {
      pinAnchorFromStore();
    },
    { immediate: true },
  );

  const nowSec = ref(0);
  let timer: ReturnType<typeof setInterval> | undefined;

  function recomputeNowSec() {
    const base = anchorServerSec.value;
    if (base != null && import.meta.client && anchorWallMs.value != null) {
      nowSec.value = base + Math.floor((Date.now() - anchorWallMs.value) / 1000);
      return;
    }
    if (base != null) {
      nowSec.value = base;
      return;
    }
    nowSec.value = Math.floor(Date.now() / 1000);
  }

  recomputeNowSec();

  if (import.meta.client) {
    onMounted(() => {
      pinAnchorFromStore();
      recomputeNowSec();
      timer = setInterval(() => {
        recomputeNowSec();
      }, 30000);
    });
    onBeforeUnmount(() => {
      if (timer) {
        clearInterval(timer);
      }
    });
  }

  const dtPcConfig = computed(() => parseDtPcFromStore(bffSsrData.value?.ding_tong_banner));

  const sceneKey = computed(() => getDtPcSceneKey(route.name as string));

  /** 当前场景块上的条带背景色（dt.json 里与 list 同级） */
  const sceneBgColor = computed(() => {
    const k = sceneKey.value;
    if (!k) {
      return '';
    }
    return dtPcConfig.value?.[k]?.bg_color ?? '';
  });

  const activeList = computed(() => {
    const scene = sceneKey.value;
    if (!scene) {
      return [] as DtPcItem[];
    }
    const block = dtPcConfig.value?.[scene];
    const list = Array.isArray(block?.list) ? (block!.list as DtPcItem[]) : [];
    return list.filter((it) => isDtPcItemActive(it, nowSec.value));
  });

  const visible = computed(() => activeList.value.length > 0);

  const speedSec = computed(() => {
    const scene = sceneKey.value;
    if (!scene) {
      return 4;
    }
    const block = dtPcConfig.value?.[scene];
    const legacy = block as Record<string, unknown> | undefined;
    const raw = block?.second_interval ?? legacy?.speed;
    const s = Number(raw);
    return s > 0 ? s : 4;
  });

  return {
    dtPcConfig,
    sceneKey,
    sceneBgColor,
    activeList,
    visible,
    speedSec,
    nowSec,
  };
}
